import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@nexus/ui";
import { useTranslation } from "react-i18next";

import type { PersonaScriptSceneDto } from "@/features/persona-opportunities/api/persona-opportunity-scripts.api";

import { PersonaScriptEditableCell } from "./PersonaScriptEditableCell.js";

export type PersonaScriptSceneField = "seeing" | "hearing" | "product" | "props";

export interface PersonaScriptSceneTableProps {
  scenes: PersonaScriptSceneDto[];
  isSelected?: boolean;
  canEdit?: boolean;
  editingKey?: string | null;
  aiEditKey?: string | null;
  savingKey?: string | null;
  flashKey?: string | null;
  flashSource?: "manual" | "ai";
  aiBusyKey?: string | null;
  onStartEdit?: (key: string) => void;
  onStopEdit?: () => void;
  onStartAi?: (key: string) => void;
  onStopAi?: () => void;
  onApplyAi?: (key: string, instruction: string) => void;
  onSaveCell?: (sceneNumber: number, field: PersonaScriptSceneField, value: string) => void;
}

function sceneRowClass(index: number, isSelected: boolean): string {
  if (isSelected && index === 0) return "hover:bg-[var(--row-selected-bg)]";
  if (index === 0) return "bg-ai-fill-light hover:bg-ai-fill-light";
  if (index % 2 === 0) return "bg-muted";
  return "bg-background";
}

function cellKey(sceneNumber: number, field: PersonaScriptSceneField): string {
  return `${sceneNumber}:${field}`;
}

export function parsePersonaScriptSceneCellKey(
  key: string,
): { sceneNumber: number; field: PersonaScriptSceneField } | null {
  const [sceneNumberRaw, field] = key.split(":");
  if (
    field !== "seeing" &&
    field !== "hearing" &&
    field !== "product" &&
    field !== "props"
  ) {
    return null;
  }
  const sceneNumber = Number(sceneNumberRaw);
  if (!Number.isInteger(sceneNumber) || sceneNumber < 0) return null;
  return { sceneNumber, field };
}

export function PersonaScriptSceneTable({
  scenes,
  isSelected = false,
  canEdit = false,
  editingKey = null,
  aiEditKey = null,
  savingKey = null,
  flashKey = null,
  flashSource = "manual",
  aiBusyKey = null,
  onStartEdit,
  onStopEdit,
  onStartAi,
  onStopAi,
  onApplyAi,
  onSaveCell,
}: PersonaScriptSceneTableProps) {
  const { t } = useTranslation();

  return (
    <div className="overflow-x-auto rounded-sm border border-border">
      <Table className="min-w-[1000px]">
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className="w-12 px-3">
              {t("personaOpportunities.scripts.sceneNumberColumn", "#")}
            </TableHead>
            <TableHead className="w-32 px-3">
              {t("personaOpportunities.scripts.sceneColumn", "Scene")}
            </TableHead>
            <TableHead className="px-3">
              {t("personaOpportunities.scripts.seeing", "Seeing")}
            </TableHead>
            <TableHead className="px-3">
              {t("personaOpportunities.scripts.hearing", "Hearing")}
            </TableHead>
            <TableHead className="w-40 px-3">
              {t("personaOpportunities.scripts.product", "Product")}
            </TableHead>
            <TableHead className="w-40 px-3">
              {t("personaOpportunities.scripts.props", "Props")}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {scenes.map((scene, index) => (
            <TableRow
              key={scene.id}
              data-state={isSelected && index === 0 ? "selected" : undefined}
              className={sceneRowClass(index, isSelected)}
            >
              <TableCell className="px-3 py-3 align-top tabular-nums text-muted-foreground">
                {scene.sceneNumber}
              </TableCell>
              <TableCell className="px-3 py-3 align-top">
                <span className="inline-block rounded-sm border border-border bg-background px-2 py-0.5 text-[11px] text-foreground">
                  {scene.sceneName}
                </span>
              </TableCell>
              {(["seeing", "hearing", "product", "props"] as const).map((field) => (
                <TableCell key={field} className="px-3 py-3 align-top leading-relaxed">
                  <PersonaScriptEditableCell
                    value={scene[field] ?? ""}
                    canEdit={canEdit}
                    isEditing={editingKey === cellKey(scene.sceneNumber, field)}
                    isAiOpen={aiEditKey === cellKey(scene.sceneNumber, field)}
                    isAiBusy={aiBusyKey === cellKey(scene.sceneNumber, field)}
                    isFlashing={
                      flashKey === cellKey(scene.sceneNumber, field) && flashSource !== "ai"
                    }
                    isFlashingAi={
                      flashKey === cellKey(scene.sceneNumber, field) && flashSource === "ai"
                    }
                    isSaving={savingKey === cellKey(scene.sceneNumber, field)}
                    onStartEdit={() => onStartEdit?.(cellKey(scene.sceneNumber, field))}
                    onStopEdit={onStopEdit}
                    onStartAi={() => onStartAi?.(cellKey(scene.sceneNumber, field))}
                    onStopAi={onStopAi}
                    onApplyAi={(instruction) =>
                      onApplyAi?.(cellKey(scene.sceneNumber, field), instruction)
                    }
                    onSave={(next) => onSaveCell?.(scene.sceneNumber, field, next)}
                  />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
